import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { endpoints } from "../endpoints";

export interface VersionReleaseSummary {
  version: string;
  releaseDate: string;
}

export interface VersionListResponse {
  latest: string;
  environment: string;
  releases: VersionReleaseSummary[];
}

export interface VersionDetail {
  version: string;
  environment: string;
  releaseDate: string;
  changelog: Record<string, string[]>;
}

type LegacyVersionResponse = VersionDetail;

const normalizeVersion = (version: string) => version.replace(/^v/i, "").trim();

const normalizeListResponse = (data: unknown): VersionListResponse => {
  const payload = data as Partial<VersionListResponse & LegacyVersionResponse>;

  if (Array.isArray(payload.releases) && payload.releases.length > 0) {
    return {
      latest: payload.latest || payload.releases[0].version,
      environment: payload.environment || "",
      releases: payload.releases,
    };
  }

  if (payload.version) {
    const release: VersionReleaseSummary = {
      version: payload.version,
      releaseDate: payload.releaseDate || "",
    };
    return {
      latest: payload.version,
      environment: payload.environment || "",
      releases: [release],
    };
  }

  return {
    latest: "",
    environment: payload.environment || "",
    releases: [],
  };
};

const normalizeDetailResponse = (
  data: unknown,
  requestedVersion: string,
): VersionDetail | null => {
  const payload = data as Partial<VersionDetail>;
  if (!payload.version || !payload.changelog) {
    return null;
  }
  if (normalizeVersion(payload.version) !== normalizeVersion(requestedVersion)) {
    return null;
  }
  return {
    version: payload.version,
    environment: payload.environment || "",
    releaseDate: payload.releaseDate || "",
    changelog: payload.changelog,
  };
};

export const useFetchVersionList = (enabled = true) => {
  return useQuery<VersionListResponse>({
    queryKey: ["version", "list"],
    queryFn: async () => {
      const response = await axios.get(endpoints.version);
      return normalizeListResponse(response.data);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useFetchVersionDetail = (version: string | undefined) => {
  return useQuery<VersionDetail>({
    queryKey: ["version", "detail", version],
    queryFn: async () => {
      if (!version) {
        throw new Error("version is required");
      }

      try {
        const response = await axios.get(endpoints.versionDetail(version));
        return response.data as VersionDetail;
      } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 404) {
          throw error;
        }

        const legacyResponse = await axios.get(endpoints.version);
        const legacyDetail = normalizeDetailResponse(legacyResponse.data, version);
        if (legacyDetail) {
          return legacyDetail;
        }

        throw error;
      }
    },
    enabled: !!version,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};
